import { ChevronDown, ChevronRight } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { AuditChanges, auditChanges } from "@/components/audit-changes"
import { PageHeader } from "@/components/layout"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { AuditEntry } from "@/lib/types"
import { formatDateTime } from "@/lib/utils"

const ACTIONS = [
  "auth.register",
  "auth.login",
  "auth.google_login",
  "account.update",
  "agent.create",
  "agent.update",
  "agent.delete",
  "mcp_connection.create",
  "mcp_connection.update",
  "mcp_connection.delete",
  "telegram.attach",
  "telegram.detach",
  "admin.user.activate",
  "admin.user.deactivate",
  "admin.ops.refresh_tools",
  "admin.ops.sync_plugins",
]

export function AdminAuditPage() {
  const { locale } = useLocale()
  const [rows, setRows] = React.useState<AuditEntry[]>([])
  const [action, setAction] = React.useState<string>("")
  const [expanded, setExpanded] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    setLoading(true)
    api
      .adminAudit({ limit: 200, action: action || undefined })
      .then((page) => setRows(page.items))
      .catch((error) =>
        toast.error(error instanceof ApiError ? error.message : "Could not load the audit log."),
      )
      .finally(() => setLoading(false))
  }, [action])

  return (
    <>
      <PageHeader title="Audit log" description="Actions across every account." />
      <div className="space-y-4 p-6">
        <select
          className="border-input bg-background rounded-md border px-3 py-1.5 text-sm"
          value={action}
          onChange={(e) => setAction(e.target.value)}
        >
          <option value="">All actions</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No entries.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Time</th>
                  <th className="px-4 py-2 text-left font-medium">Actor</th>
                  <th className="px-4 py-2 text-left font-medium">Action</th>
                  <th className="px-4 py-2 text-left font-medium">Target</th>
                  <th className="px-4 py-2 text-left font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const changes = auditChanges(row)
                  const isOpen = expanded === row.id
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className={changes ? "hover:bg-accent/40 cursor-pointer border-t" : "border-t"}
                        onClick={changes ? () => setExpanded(isOpen ? null : row.id) : undefined}
                      >
                        <td className="text-muted-foreground px-4 py-2">
                          {formatDateTime(row.created_at, locale)}
                        </td>
                        <td className="px-4 py-2">{row.actor_email}</td>
                        <td className="px-4 py-2 font-medium">
                          <span className="flex items-center gap-1.5">
                            {changes ? (
                              isOpen ? (
                                <ChevronDown className="size-3.5 shrink-0" />
                              ) : (
                                <ChevronRight className="size-3.5 shrink-0" />
                              )
                            ) : (
                              <span className="inline-block w-3.5" />
                            )}
                            {row.action}
                          </span>
                        </td>
                        <td className="px-4 py-2">{row.target_label ?? row.target_type ?? "—"}</td>
                        <td className="text-muted-foreground px-4 py-2">{row.ip_address ?? "—"}</td>
                      </tr>
                      {changes && isOpen && (
                        <tr className="bg-muted/30 border-t">
                          <td colSpan={5} className="px-4 py-3 pl-11">
                            <AuditChanges changes={changes} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
